import React, { FC, useState } from 'react';
import styles from './MegaMenu.module.scss';
import { GridContainer, GridElement } from 'components/Grid';
import { ServiceSwitch } from 'components/ServiceSwitch';
import { useAreenaService } from 'contexts/AreenaServiceContext';
import { Language, List, Service } from '@yleisradio/areena-types';
import {
  BrowseMenuContent,
  BrowseMenuContentBlock,
} from '@yleisradio/areena-types/domain/v2/browseMenuContent';
import { CategoryLinkControl } from 'components/CategoryLinkControl/CategoryLinkControl';
import { getControlKey } from 'utils/control';
import { TextLinkControl } from 'components/TextLinkControl/TextLinkControl';
import { ImageLinkControl } from 'components/ImageLinkControl/ImageLinkControl';
import { useUILanguage } from 'hooks/useUILanguage';
import { createSearchResultsPointer } from 'services/areena-api/pointer';
import { MegaMenuSearchList } from './MegaMenuSearchList/MegaMenuSearchList';
import { useCustomEventAnalytics } from 'hooks/analytics';

export const MENU_ID = 'mega-menu';
import { useTranslation } from 'hooks/useTranslation';

type Props = {
  searchQuery: string | null;
  closeMenu(): void;
  browseMenuContent: BrowseMenuContent | null;
};

const findBlock =
  (blockType: BrowseMenuContentBlock['type']) =>
  (block: BrowseMenuContentBlock) =>
    block.type === blockType;

const MegaMenu: FC<Props> = ({ searchQuery, closeMenu, browseMenuContent }) => {
  const { areenaService } = useAreenaService();
  const [activeService, setActiveService] = useState<Service>(
    areenaService ?? 'tv'
  );

  const trackEvent = useCustomEventAnalytics();

  const language = useUILanguage();
  const t = useTranslation();

  if (!browseMenuContent) return null;

  const isPodcastService = activeService === 'radio';

  const activeBlocks = isPodcastService
    ? browseMenuContent['podcastBlocks']
    : browseMenuContent['tvBlocks'];
  const categoryImageLinkBlock = activeBlocks.find(
    findBlock('categoryImageLinks')
  );
  const categoryLinkBlock = activeBlocks.find(findBlock('categoryLinks'));
  const textLinkBlock = activeBlocks.find(findBlock('textLinks'));

  return (
    <div
      role="dialog"
      aria-label={t('searchAndBrowse')}
      className={styles.container}
      id={MENU_ID}
    >
      <div className={styles.innerContainer}>
        <GridContainer>
          <GridElement mobile={12}>
            <ServiceSwitch
              activeService={activeService}
              setActiveService={setActiveService}
              onClick={() => trackEvent('header-service-switch-click')}
            />
          </GridElement>
        </GridContainer>

        {!searchQuery ? (
          <GridContainer>
            {categoryImageLinkBlock &&
              categoryImageLinkBlock.controls?.map((control) => (
                <GridElement
                  key={getControlKey(control)}
                  mobile={6}
                  mobileLandscape={4}
                  tablet={isPodcastService ? 3 : 4}
                  desktopSmall={isPodcastService ? 6 : 8}
                >
                  <ImageLinkControl control={control} onClick={closeMenu} />
                </GridElement>
              ))}
          </GridContainer>
        ) : (
          <MegaMenuSearchList
            lists={{
              tv: createSearchResultsList(searchQuery, language, 'tv'),
              radio: createSearchResultsList(searchQuery, language, 'radio'),
            }}
            searchQuery={searchQuery}
            service={activeService}
            onClick={closeMenu}
          />
        )}

        {categoryLinkBlock && !searchQuery && (
          <div
            className={styles.categoryLinksContainer}
            role="group"
            aria-label={t('categories')}
          >
            {categoryLinkBlock.controls?.map((control) => (
              <CategoryLinkControl
                key={getControlKey(control)}
                control={control}
                onClick={closeMenu}
              />
            ))}
          </div>
        )}

        {textLinkBlock && !searchQuery && (
          <div className={styles.textLinkContainer}>
            {textLinkBlock.controls?.map((control) => (
              <TextLinkControl
                key={getControlKey(control)}
                control={control}
                onClick={closeMenu}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { MegaMenu };

function createSearchResultsList(
  query: string,
  language: Language,
  service: Service
): List {
  return {
    controls: [],
    filters: [],
    presentation: 'medium-grid',
    source: createSearchResultsPointer(query, language, service),
    style: {
      image: service === 'radio' ? '1:1' : '16:9',
      layout: 'horizontal',
      size: 'small',
    },
    type: 'list',
  };
}
